import { Grid, Tooltip, Typography, makeStyles, type GridSize } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Colors } from '@rentguru/commons-utils';
import clsx from 'clsx';
import { CSSProperties, FC, SVGProps } from 'react';

import { Button } from '../basics';
import { StyledCheckbox } from '../checkboxes';

import { HEADER_HEIGHT, WIDTH_CHECKBOX } from './customTableUtils';

export const useTableHeaderStyles = makeStyles((theme) => ({
  text: {
    fontSize: 12,
    lineHeight: 1.5,
    color: Colors.SLATE_GREY,
    fontWeight: 300,
  },
  textDisabled: {
    color: Colors.SILVER,
  },
  textHover: {
    '&:hover': {
      color: Colors.SILVER,
      cursor: 'pointer',
    },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
  },
  TableHeader: {
    height: HEADER_HEIGHT,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    border: '1px solid transparent',
    boxSizing: 'border-box',
  },
  icon: {
    height: 16,
    marginRight: 4,
    marginLeft: 4,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    userSelect: 'none',
    width: 16,
  },
  iconDirectionDesc: {
    transform: 'rotate(0deg)',
  },
  iconDirectionAsc: {
    transform: 'rotate(180deg)',
  },
}));

export interface TableHeaderColumn {
  xs: GridSize;
  name: string;
  sortable: boolean;
  onClick?: () => void;
  showExpandMore?: boolean;
  ascending?: boolean;
  info?: boolean;
  infoText?: string;
  disabled?: boolean;
  style?: CSSProperties;
}

export interface CustomTableHeaderProps {
  InfoIcon: FC<SVGProps<SVGSVGElement>>;
  hasCheckbox?: boolean;
  checkboxIndeterminate?: boolean;
  checkboxChecked?: boolean;
  checkboxOnClick?: () => void;
  columns: TableHeaderColumn[];
  style?: CSSProperties;
  hasVerticalScrollbar?: boolean;
}

export const CustomTableHeader: React.FC<CustomTableHeaderProps> = ({
  hasCheckbox = false,
  checkboxIndeterminate,
  checkboxChecked,
  checkboxOnClick,
  columns,
  InfoIcon,
  style,
  hasVerticalScrollbar = false,
}) => {
  const classes = useTableHeaderStyles();
  return (
    <Grid className={classes.TableHeader} style={{ paddingRight: hasVerticalScrollbar ? 16 : 0, ...style }}>
      {hasCheckbox && (
        <Grid item className={classes.item} style={{ width: WIDTH_CHECKBOX }}>
          <StyledCheckbox checked={checkboxChecked} onClick={checkboxOnClick} indeterminate={checkboxIndeterminate} />
        </Grid>
      )}
      <Grid container>
        {columns?.map((column) => {
          if (column.disabled) {
            return (
              <Grid item xs={column.xs} className={clsx(classes.item)} key={column.name} style={column.style}>
                <Typography className={clsx(classes.text, classes.textDisabled)}>{column.name}</Typography>
                {column.info && (
                  <Tooltip title={column.infoText!} placement="bottom" arrow>
                    <Grid>
                      <InfoIcon style={{ height: 16, width: 16, fill: Colors.SILVER }} />
                    </Grid>
                  </Tooltip>
                )}
              </Grid>
            );
          }
          if (column.sortable) {
            return (
              <Grid item xs={column.xs} className={clsx(classes.item)} key={column.name} style={column.style}>
                <Button
                  className={clsx(classes.text, classes.textHover)}
                  onClick={column.onClick}
                  style={{ padding: 0, margin: 0 }}
                >
                  <Typography className={clsx(classes.text, column.disabled && classes.textDisabled)}>
                    {column.name}
                  </Typography>
                  {column.info && (
                    <Tooltip title={column.infoText!} placement="bottom" arrow>
                      <Grid>
                        <InfoIcon
                          style={{ height: 16, width: 16, paddingLeft: 5, paddingTop: 5, fill: Colors.SLATE_GREY }}
                        />
                      </Grid>
                    </Tooltip>
                  )}
                  {column.showExpandMore && (
                    <ExpandMore
                      className={clsx(
                        classes.icon,
                        column.ascending ? classes.iconDirectionAsc : classes.iconDirectionDesc
                      )}
                    />
                  )}
                </Button>
              </Grid>
            );
          }
          return (
            <Grid item xs={column.xs} className={clsx(classes.item)} key={column.name} style={column.style}>
              <Typography className={clsx(classes.text)}>{column.name}</Typography>
              {column.info && (
                <Tooltip title={column.infoText!} placement="bottom" arrow>
                  <Grid>
                    <InfoIcon
                      style={{ height: 16, width: 16, paddingLeft: 5, paddingTop: 5, fill: Colors.SLATE_GREY }}
                    />
                  </Grid>
                </Tooltip>
              )}
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};
